import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Heart, Gift, Stethoscope, ShoppingBag, Star, DollarSign } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Footer from "@/components/sections/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  pet_food: ShoppingBag,
  comfort_bundle: Gift,
  care_package: Star,
  vet_fund: Stethoscope,
  guardian_angel: Heart,
};

export default function DonatePage() {
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/donation-packages`)
      .then((r) => setPackages(r.data))
      .catch(() => toast.error("Could not load donation packages."));
  }, []);

  const handleDonate = async () => {
    setLoading(true);
    const origin = window.location.origin;

    try {
      let res;
      if (isCustom) {
        const amt = parseFloat(customAmount);
        if (!amt || amt < 1) {
          toast.error("Please enter an amount of at least $1.00");
          setLoading(false);
          return;
        }
        res = await axios.post(`${API}/donate/custom-checkout`, {
          amount: amt,
          origin_url: origin,
        });
      } else {
        if (!selectedPkg) {
          toast.error("Please select a donation package.");
          setLoading(false);
          return;
        }
        res = await axios.post(`${API}/donate/checkout`, {
          package_id: selectedPkg,
          origin_url: origin,
        });
      }

      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="donate-page" className="min-h-screen">
      <div className="pt-28 pb-24 md:pt-36 md:pb-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="font-body text-xs font-bold tracking-[0.2em] uppercase text-[#C0603A] mb-4">
              Support 333Oreo Foundation
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none text-[#2C2C2A] font-medium mb-4">
              Make a Donation
            </h1>
            <p className="font-body text-base sm:text-lg text-[#6C6B66] max-w-xl mx-auto">
              Your generosity helps us provide food, supplies, and veterinary resources
              to fur families in need. Every contribution keeps Oreo's legacy alive.
            </p>
          </motion.div>

          {/* Packages */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
            {packages.map((pkg, i) => {
              const Icon = iconMap[pkg.id] || Heart;
              const isSelected = selectedPkg === pkg.id && !isCustom;
              return (
                <motion.button
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  onClick={() => {
                    setSelectedPkg(pkg.id);
                    setIsCustom(false);
                  }}
                  data-testid={`donate-package-${pkg.id}`}
                  className={`text-left p-6 rounded-3xl border-2 transition-all duration-300 ${
                    isSelected
                      ? "border-[#C0603A] bg-[#C0603A]/5 shadow-[0_12px_48px_rgba(192,96,58,0.12)]"
                      : "border-[#E8E3DD] bg-[#F2EFEB] hover:border-[#C0603A]/40"
                  }`}
                >
                  <Icon className={`w-7 h-7 mb-3 ${isSelected ? "text-[#C0603A]" : "text-[#8FA396]"}`} />
                  <p className="font-heading text-2xl text-[#2C2C2A] font-medium">${pkg.amount.toFixed(0)}</p>
                  <p className="font-body text-sm font-semibold text-[#3A3A38] mt-1">{pkg.name}</p>
                  <p className="font-body text-xs text-[#6C6B66] mt-1 leading-relaxed">{pkg.description}</p>
                </motion.button>
              );
            })}

            {/* Custom Amount */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              onClick={() => setIsCustom(true)}
              data-testid="donate-custom-toggle"
              className={`text-left p-6 rounded-3xl border-2 transition-all duration-300 ${
                isCustom
                  ? "border-[#C0603A] bg-[#C0603A]/5 shadow-[0_12px_48px_rgba(192,96,58,0.12)]"
                  : "border-[#E8E3DD] bg-[#F2EFEB] hover:border-[#C0603A]/40"
              }`}
            >
              <DollarSign className={`w-7 h-7 mb-3 ${isCustom ? "text-[#C0603A]" : "text-[#8FA396]"}`} />
              <p className="font-heading text-2xl text-[#2C2C2A] font-medium">Custom</p>
              <p className="font-body text-sm font-semibold text-[#3A3A38] mt-1">Choose Your Amount</p>
              <p className="font-body text-xs text-[#6C6B66] mt-1">Any amount makes a difference</p>
            </motion.button>
          </div>

          {/* Custom Amount Input */}
          {isCustom && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mb-8"
            >
              <div className="relative max-w-xs mx-auto">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6C6B66]" />
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="Enter amount"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="w-full bg-white border-2 border-[#E8E3DD] rounded-2xl pl-12 pr-4 py-4 font-body text-lg text-[#3A3A38] placeholder:text-[#6C6B66]/50 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow text-center"
                  data-testid="donate-custom-amount-input"
                />
              </div>
            </motion.div>
          )}

          {/* Donate Button */}
          <div className="text-center">
            <button
              onClick={handleDonate}
              disabled={loading || (!selectedPkg && !isCustom)}
              data-testid="donate-submit-button"
              className="bg-[#C0603A] text-white hover:bg-[#A64D29] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 rounded-full px-10 py-4 font-body font-semibold text-lg inline-flex items-center gap-3 hover:shadow-[0_12px_48px_rgba(192,96,58,0.25)]"
            >
              <Heart className="w-5 h-5" />
              {loading ? "Redirecting to payment..." : "Donate with Love"}
            </button>
            <p className="font-body text-xs text-[#6C6B66] mt-4">
              Secure payment powered by Stripe. You will be redirected to complete your donation.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
